import { Controller } from "@hotwired/stimulus"

// FD-891: a <details> section that remembers whether it was left open or closed, per
// browser, under its key. The server renders the section's default, open or closed
// (FD-906's Completed sprints starts closed); any stored value wins on connect.
// Every localStorage touch is guarded — private mode, blocked storage or a quota error
// must cost the preference, never the page.
//
// FD-906: a Turbo morph never rewrites `open`. A same-URL redirect (the Sprints page's
// "Generate sprints" posts back to itself) or a broadcast refresh morphs the page without
// re-running connect(), and the morph would copy the server's `open` back onto the
// element. That fires `toggle`, and save() would then write the server default over the
// reader's stored choice. So the controller refuses the morph of this element's own `open`
// attribute; descendants' morph events bubble here too and are left alone, and the rest
// of the section still morphs.
export default class extends Controller {
  static values = { key: String }

  connect() {
    const stored = this.#read()
    if (stored !== null) this.element.open = stored === "open"
    this.element.addEventListener("turbo:before-morph-attribute", this.#keepOpen)
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-morph-attribute", this.#keepOpen)
  }

  // Wired to the element's own `toggle` event, which also fires after connect() sets
  // `open`; writing back the value just read is harmless.
  save() {
    this.#write(this.element.open ? "open" : "closed")
  }

  #keepOpen = (event) => {
    if (event.target === this.element && event.detail?.attributeName === "open") event.preventDefault()
  }

  get #storageKey() {
    return `fd:details:${this.keyValue}`
  }

  #read() {
    try {
      return window.localStorage.getItem(this.#storageKey)
    } catch {
      return null
    }
  }

  #write(value) {
    try {
      window.localStorage.setItem(this.#storageKey, value)
    } catch {
      // Storage unavailable: the section still works, it just won't be remembered.
    }
  }
}
